import { ORIGIN } from "@shared/api";
import type { Uuid } from "@shared/api";

/**
 * Переход к сообщению в ленте канала.
 *
 * Только по загруженному окну: сообщение могло остаться далеко выше, и
 * подгрузка ради него утащила бы ленту с того места, где человек читает. Нет
 * в окне — не уводим никуда, и это честнее прыжка вникуда.
 *
 * Общая на два места: по цитате ответа и по пункту меню ветки. Разойтись им
 * нельзя — переход один и тот же.
 */
export function jumpToMessage(messageId: string) {
  const element = document.querySelector(`.feed [data-message="${messageId}"]`);
  if (!element) return;

  element.scrollIntoView({ block: "center", behavior: "smooth" });
  element.classList.add("flash");
  setTimeout(() => element.classList.remove("flash"), 1200);
}

/**
 * Ссылка на сообщение — как в Discord: `…/#channels/<сервер или @me>/<канал>/<сообщение>`.
 *
 * Через `#`, а не путём: страницу по такому адресу отдаёт любой веб-сервер без
 * настройки, а приложение читает остаток само. Сервер в ссылке нужен, чтобы
 * перейти, не спрашивая, чей это канал.
 */
export interface MessageLink {
  /** `null` — личная переписка. */
  serverId: Uuid | null;
  channelId: Uuid;
  /** `null` — ссылка на сам канал, как «Копировать ссылку» в меню канала. */
  messageId: Uuid | null;
  /**
   * Ссылка на ветку — «Копировать ссылку» в меню ветки:
   * `…/#channels/<сервер>/<канал>/threads/<ветка>`. У Discord ветка — канал,
   * у нас она живёт под каналом, и канал в ссылке нужен, чтобы её открыть.
   */
  threadId?: Uuid;
}

const ID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const PATH = new RegExp(`#channels/(@me|${ID})/(${ID})(?:/threads/(${ID})|/(${ID}))?$`, "i");

/** Откуда ссылки: в вебе — сама страница, в настольном — адрес API, он же сайт. */
function base(): string {
  return ORIGIN || (typeof location === "undefined" ? "" : location.origin);
}

export function messageLink({ serverId, channelId, messageId, threadId }: MessageLink): string {
  const tail = threadId ? `/threads/${threadId}` : messageId ? `/${messageId}` : "";
  return `${base()}/#channels/${serverId ?? "@me"}/${channelId}${tail}`;
}

/**
 * Ссылка ли это на сообщение здесь. Чужой сайт с тем же видом адреса — не
 * наша ссылка: переход по ней внутри приложения подменил бы чужую страницу
 * нашей.
 */
export function readMessageLink(href: string): MessageLink | null {
  const at = href.indexOf("#");
  if (at < 0 || href.slice(0, at).replace(/\/$/, "") !== base()) return null;

  const found = PATH.exec(href.slice(at));
  if (!found) return null;

  return {
    serverId: found[1].toLowerCase() === "@me" ? null : found[1],
    channelId: found[2],
    messageId: found[4] ?? null,
    ...(found[3] ? { threadId: found[3] } : {}),
  };
}

/**
 * Ссылка-приглашение — как discord.gg/код: `…/?invite=код`. Такую понимает и
 * экран входа (приглашает ещё не зарегистрированного), и лента (карточка).
 */
export function inviteLink(code: string): string {
  return `${base()}/?invite=${code}`;
}

/** Код — десять знаков из алфавита без похожих символов, как на сервере. */
const CODE = "[2-9a-hjkmnp-z]{10}";

/**
 * Коды приглашений в тексте — только с нашего сайта: чужая ссылка того же
 * вида — не наше приглашение. Читаются оба вида, как на экране входа.
 */
export function readInviteCodes(text: string): string[] {
  // Дешёвая проверка до регулярки: в почти любом сообщении приглашения нет.
  if (!text.includes("invite")) return [];
  const site = base().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  // Адрес сайта — строкой, не «чем угодно»: перебор на тексте из скобок не нужен.
  // Код кончается там, где кончаются знаки кода: «…?invite=» плюс одиннадцать — не код.
  const pattern = new RegExp(`(?:^|[\\s(<])${site}/?(?:\\?invite=|#/invite/)(${CODE})(?![2-9a-z])`, "gi");
  const found = new Set<string>();
  for (const match of text.matchAll(pattern)) found.add(match[1].toLowerCase());
  return [...found];
}

/** Код из того, что вставили в поле «Код приглашения»: сам код или ссылка на него. */
export function inviteCodeOf(raw: string): string {
  const trimmed = raw.trim();
  const fromLink = /(?:invite=|\/invite\/)([2-9a-hjkmnp-z]{10})/i.exec(trimmed);
  return (fromLink ? fromLink[1] : trimmed).toLowerCase();
}

/**
 * Приглашение бота — как ссылка OAuth2 Discord, но на нашем сайте:
 * `…/#oauth2/authorize?client_id=…&permissions=…&scope=bot`.
 */
export function botInviteLink(clientId: string, permissions: string): string {
  const query = new URLSearchParams({ client_id: clientId, permissions, scope: "bot applications.commands" });
  return `${base()}/#oauth2/authorize?${query.toString()}`;
}

export interface BotInvite {
  clientId: string;
  permissions: string | null;
}

/** Ссылка приглашения бота из адреса страницы; не она — `null`. */
export function readBotInvite(href: string): BotInvite | null {
  const at = href.indexOf("#oauth2/authorize?");
  if (at < 0) return null;
  const query = new URLSearchParams(href.slice(at + "#oauth2/authorize?".length));
  const clientId = query.get("client_id");
  return clientId && /^\d+$/.test(clientId) ? { clientId, permissions: query.get("permissions") } : null;
}

/** Корень API для разработчиков ботов: по нему ходят discord.js и discord.py. */
export function apiBase(): string {
  return `${base()}/api`;
}

